import type { ReactNode } from 'react'
import { Button } from './Button'
import { Icon, type IconName } from './Icon'
import css from './StateBlock.module.css'

/**
 * Blok keadaan.
 *
 * Tiga komponen yang sengaja terpisah dan TIDAK boleh saling menyamar.
 * Antarmuka lama menampilkan "Belum ada link" ketika kueri basis data gagal —
 * pengguna diberi tahu bahwa ia belum punya apa-apa, padahal sistemnya sedang
 * rusak. Kesalahan seperti itu menutupi gangguan nyata selama berhari-hari.
 */

interface BaseProps {
  title: string
  message?: string
  children?: ReactNode
  compact?: boolean
}

/** Tidak ada data — dan itu wajar. Selalu sertakan satu tindakan yang jelas. */
export function EmptyState({
  title,
  message,
  icon = 'inbox',
  action,
  compact,
  children,
}: BaseProps & { icon?: IconName; action?: ReactNode }) {
  return (
    <div className={`${css.block} ${compact ? css.compact : ''}`}>
      <div className={`${css.icon} ${css.neutral}`}>
        <Icon name={icon} size={26} />
      </div>
      <div className={css.title}>{title}</div>
      {message && <p className={css.message}>{message}</p>}
      {children}
      {action && <div className={css.actions}>{action}</div>}
    </div>
  )
}

/** Sesuatu gagal. Selalu sertakan jalan untuk mencoba lagi. */
export function ErrorState({
  title = 'Gagal memuat data',
  message = 'Terjadi kesalahan saat mengambil data. Silakan coba lagi.',
  onRetry,
  details,
  compact,
}: Partial<BaseProps> & {
  onRetry?: () => void
  /** Rincian teknis — disembunyikan di balik pengungkap. */
  details?: string
}) {
  return (
    <div className={`${css.block} ${compact ? css.compact : ''}`} role="alert">
      <div className={`${css.icon} ${css.error}`}>
        <Icon name="error" size={26} />
      </div>
      <div className={css.title}>{title}</div>
      <p className={css.message}>{message}</p>

      {details && (
        <details className={css.details}>
          <summary>Rincian teknis</summary>
          <div className={css.detailsBody}>{details}</div>
        </details>
      )}

      {onRetry && (
        <div className={css.actions}>
          <Button variant="secondary" icon="refresh" onClick={onRetry}>
            Coba lagi
          </Button>
        </div>
      )}
    </div>
  )
}

/** Tidak punya izin. Selalu jelaskan sebabnya dan beri jalan keluar. */
export function PermissionState({
  title = 'Anda tidak memiliki akses',
  message,
  action,
  compact,
}: Partial<BaseProps> & { action?: ReactNode }) {
  return (
    <div className={`${css.block} ${compact ? css.compact : ''}`}>
      <div className={`${css.icon} ${css.locked}`}>
        <Icon name="lock" size={26} />
      </div>
      <div className={css.title}>{title}</div>
      {message && <p className={css.message}>{message}</p>}
      {action && <div className={css.actions}>{action}</div>}
    </div>
  )
}

/**
 * Kerangka pemuatan berbentuk baris berkas.
 *
 * Menggantikan pemintal di tengah layar: bentuknya menyerupai isi yang akan
 * datang, sehingga tata letak tidak melompat saat data tiba.
 */
export function SkeletonList({ rows = 8 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div className={css.skeletonRow} key={i}>
          <div className={css.shimmer} style={{ width: 24, height: 24, borderRadius: 6 }} />
          <div
            className={css.shimmer}
            style={{
              // Lebar bervariasi agar terlihat seperti nama berkas sungguhan,
              // bukan deretan batang yang identik.
              width: `${38 + ((i * 37) % 42)}%`,
              height: 13,
            }}
          />
          <div className={css.shimmer} style={{ width: 56, height: 11, marginLeft: 'auto' }} />
          <div className={css.shimmer} style={{ width: 84, height: 11 }} />
        </div>
      ))}
      <span className="sr-only">Memuat data</span>
    </div>
  )
}

/** Kerangka pemuatan berbentuk kisi kartu. */
export function SkeletonGrid({ cards = 12 }: { cards?: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
      }}
    >
      {Array.from({ length: cards }, (_, i) => (
        <div key={i}>
          <div className={css.shimmer} style={{ aspectRatio: '1', borderRadius: 'var(--radius-lg)' }} />
          <div
            className={css.shimmer}
            style={{ height: 11, marginTop: 'var(--space-2)', width: `${55 + ((i * 23) % 40)}%` }}
          />
        </div>
      ))}
      <span className="sr-only">Memuat data</span>
    </div>
  )
}
